import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Calculator, Lock, DollarSign, Truck, 
  TrendingUp, Calendar, FileText, BarChart3, Target,
  Crown, ChevronRight, AlertCircle, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calculator Card Component
const CalculatorCard = ({ tool, onClick, locked }) => {
  const iconMap = {
    rpm_calculator: TrendingUp,
    deadhead_calculator: Truck,
    break_even_calculator: Target,
    factoring_calculator: DollarSign,
    fuel_savings_estimator: BarChart3,
    compliance_calendar: Calendar,
    carrier_packet_builder: FileText,
    revenue_dashboard: BarChart3,
  };
  
  const Icon = iconMap[tool.tool_id] || Calculator;
  
  return (
    <button
      onClick={onClick}
      disabled={locked}
      className={`w-full p-6 text-left transition-all border ${
        locked 
          ? 'bg-zinc-900/50 border-zinc-800 cursor-not-allowed opacity-60' 
          : 'bg-[#121212] border-white/10 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5'
      }`}
      data-testid={`tool-${tool.tool_id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 flex items-center justify-center ${locked ? 'bg-zinc-800' : 'bg-[#D4AF37]/20'}`}>
            {locked ? (
              <Lock className="w-6 h-6 text-zinc-500" />
            ) : (
              <Icon className="w-6 h-6 text-[#D4AF37]" />
            )}
          </div>
          <div>
            <h3 className="text-white font-bold">{tool.tool_name}</h3>
            <p className="text-white/50 text-sm mt-1">{tool.description}</p>
            {locked && (
              <p className="text-amber-400 text-xs mt-2 uppercase">
                Unlock with {tool.unlock_tier} tier
              </p>
            )}
          </div>
        </div>
        {!locked && <ChevronRight className="w-5 h-5 text-white/30" />}
      </div>
    </button>
  );
};

// RPM Calculator Component
const RPMCalculator = ({ onClose }) => {
  const [inputs, setInputs] = useState({
    total_rate: '',
    total_miles: '',
    fuel_cost: '',
    deadhead_miles: '0'
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculate = async () => {
    if (!inputs.total_rate || !inputs.total_miles) return;
    
    setIsCalculating(true);
    try {
      const res = await axios.post(`${API}/build-system/calculator/rpm`, {
        total_rate: parseFloat(inputs.total_rate),
        total_miles: parseFloat(inputs.total_miles),
        fuel_cost: parseFloat(inputs.fuel_cost) || 0,
        deadhead_miles: parseFloat(inputs.deadhead_miles) || 0
      });
      setResult(res.data);
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Total Rate ($)</label>
          <input
            type="number"
            value={inputs.total_rate}
            onChange={(e) => setInputs(prev => ({ ...prev, total_rate: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="2500"
          />
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Loaded Miles</label>
          <input
            type="number"
            value={inputs.total_miles}
            onChange={(e) => setInputs(prev => ({ ...prev, total_miles: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="800"
          />
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Deadhead Miles</label>
          <input
            type="number"
            value={inputs.deadhead_miles}
            onChange={(e) => setInputs(prev => ({ ...prev, deadhead_miles: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="100"
          />
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Fuel Cost ($)</label>
          <input
            type="number"
            value={inputs.fuel_cost}
            onChange={(e) => setInputs(prev => ({ ...prev, fuel_cost: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="400"
          />
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={isCalculating || !inputs.total_rate || !inputs.total_miles}
        className="w-full py-4 bg-[#D4AF37] text-black font-bold text-lg hover:bg-[#F5D47B] disabled:opacity-50"
      >
        {isCalculating ? 'Calculating...' : 'Calculate RPM'}
      </button>

      {result && (
        <div className="bg-[#0A0A0A] border border-white/10 p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-white/50 text-sm uppercase">Gross RPM</p>
              <p className="text-3xl font-bold text-[#D4AF37]">${result.gross_rpm}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm uppercase">Effective RPM</p>
              <p className="text-3xl font-bold text-white">${result.effective_rpm}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm uppercase">Net RPM</p>
              <p className="text-3xl font-bold text-green-400">${result.net_rpm}</p>
            </div>
          </div>
          
          <div className={`p-4 ${result.analysis.is_profitable ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center gap-2">
              {result.analysis.is_profitable ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-400" />
              )}
              <span className={result.analysis.is_profitable ? 'text-green-400' : 'text-red-400'}>
                {result.analysis.recommendation}
              </span>
            </div>
            {result.analysis.deadhead_impact > 0 && (
              <p className="text-white/50 text-sm mt-2">
                Deadhead impact: -${result.analysis.deadhead_impact}/mile
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Deadhead Calculator Component
const DeadheadCalculator = ({ onClose }) => {
  const [inputs, setInputs] = useState({
    loaded_miles: '',
    deadhead_miles: '',
    rate: '',
    cost_per_mile: '0.50'
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculate = async () => {
    if (!inputs.loaded_miles || !inputs.rate) return;
    
    setIsCalculating(true);
    try {
      const res = await axios.post(`${API}/build-system/calculator/deadhead`, {
        loaded_miles: parseFloat(inputs.loaded_miles),
        deadhead_miles: parseFloat(inputs.deadhead_miles) || 0,
        rate: parseFloat(inputs.rate),
        cost_per_mile: parseFloat(inputs.cost_per_mile)
      });
      setResult(res.data);
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Loaded Miles</label>
          <input
            type="number"
            value={inputs.loaded_miles}
            onChange={(e) => setInputs(prev => ({ ...prev, loaded_miles: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="800"
          />
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Deadhead Miles</label>
          <input
            type="number"
            value={inputs.deadhead_miles}
            onChange={(e) => setInputs(prev => ({ ...prev, deadhead_miles: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="150"
          />
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Load Rate ($)</label>
          <input
            type="number"
            value={inputs.rate}
            onChange={(e) => setInputs(prev => ({ ...prev, rate: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="2400"
          />
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Cost/Mile ($)</label>
          <input
            type="number"
            step="0.01"
            value={inputs.cost_per_mile}
            onChange={(e) => setInputs(prev => ({ ...prev, cost_per_mile: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="0.50"
          />
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={isCalculating || !inputs.loaded_miles || !inputs.rate}
        className="w-full py-4 bg-[#D4AF37] text-black font-bold text-lg hover:bg-[#F5D47B] disabled:opacity-50"
      >
        {isCalculating ? 'Calculating...' : 'Calculate Deadhead Impact'}
      </button>

      {result && (
        <div className="bg-[#0A0A0A] border border-white/10 p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-[#121212]">
              <p className="text-white/50 text-sm uppercase">Deadhead %</p>
              <p className={`text-3xl font-bold ${result.deadhead_percentage <= 10 ? 'text-green-400' : result.deadhead_percentage <= 20 ? 'text-amber-400' : 'text-red-400'}`}>
                {result.deadhead_percentage}%
              </p>
            </div>
            <div className="text-center p-4 bg-[#121212]">
              <p className="text-white/50 text-sm uppercase">Deadhead Cost</p>
              <p className="text-3xl font-bold text-red-400">${result.deadhead_cost}</p>
            </div>
            <div className="text-center p-4 bg-[#121212]">
              <p className="text-white/50 text-sm uppercase">Loaded RPM</p>
              <p className="text-3xl font-bold text-[#D4AF37]">${result.loaded_rpm}</p>
            </div>
            <div className="text-center p-4 bg-[#121212]">
              <p className="text-white/50 text-sm uppercase">Effective RPM</p>
              <p className="text-3xl font-bold text-white">${result.effective_rpm}</p>
            </div>
          </div>
          
          <div className="text-center p-4 bg-[#121212]">
            <p className="text-white/50 text-sm uppercase mb-2">Net Profit</p>
            <p className={`text-4xl font-bold ${result.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${result.net_profit}
            </p>
          </div>
          
          <div className={`p-4 ${result.analysis.is_acceptable ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
            <p className={result.analysis.is_acceptable ? 'text-green-400' : 'text-amber-400'}>
              {result.analysis.recommendation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Break-Even Calculator (Professional+ tier)
const BreakEvenCalculator = ({ onClose }) => {
  const [inputs, setInputs] = useState({
    monthly_fixed_costs: '',
    variable_cost_per_mile: '',
    average_rpm: ''
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculate = async () => {
    if (!inputs.monthly_fixed_costs || !inputs.average_rpm) return;
    
    setIsCalculating(true);
    try {
      const res = await axios.post(`${API}/build-system/calculator/break-even`, {
        monthly_fixed_costs: parseFloat(inputs.monthly_fixed_costs),
        variable_cost_per_mile: parseFloat(inputs.variable_cost_per_mile) || 0,
        average_rpm: parseFloat(inputs.average_rpm)
      });
      setResult(res.data);
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Monthly Fixed Costs ($)</label>
          <input
            type="number"
            value={inputs.monthly_fixed_costs}
            onChange={(e) => setInputs(prev => ({ ...prev, monthly_fixed_costs: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="5000"
          />
          <p className="text-white/30 text-xs mt-1">Insurance, payments, etc.</p>
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Variable Cost/Mile ($)</label>
          <input
            type="number"
            step="0.01"
            value={inputs.variable_cost_per_mile}
            onChange={(e) => setInputs(prev => ({ ...prev, variable_cost_per_mile: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="0.50"
          />
          <p className="text-white/30 text-xs mt-1">Fuel, maintenance, etc.</p>
        </div>
        <div>
          <label className="text-white/50 text-sm uppercase mb-2 block">Average RPM ($)</label>
          <input
            type="number"
            step="0.01"
            value={inputs.average_rpm}
            onChange={(e) => setInputs(prev => ({ ...prev, average_rpm: e.target.value }))}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-lg"
            placeholder="2.80"
          />
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={isCalculating || !inputs.monthly_fixed_costs || !inputs.average_rpm}
        className="w-full py-4 bg-[#D4AF37] text-black font-bold text-lg hover:bg-[#F5D47B] disabled:opacity-50"
      >
        {isCalculating ? 'Calculating...' : 'Calculate Break-Even'}
      </button>

      {result && !result.error && (
        <div className="bg-[#0A0A0A] border border-white/10 p-6 space-y-4">
          <div className="text-center p-6 bg-[#121212]">
            <p className="text-white/50 text-sm uppercase mb-2">Monthly Break-Even Miles</p>
            <p className="text-5xl font-bold text-[#D4AF37]">{result.break_even_miles?.toLocaleString()}</p>
            <p className="text-white/50 mt-2">miles per month to cover costs</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-[#121212]">
              <p className="text-white/50 text-xs uppercase">Weekly Target</p>
              <p className="text-xl font-bold text-white">{result.analysis?.weekly_miles_needed?.toLocaleString()} mi</p>
            </div>
            <div className="p-4 bg-[#121212]">
              <p className="text-white/50 text-xs uppercase">Daily Target</p>
              <p className="text-xl font-bold text-white">{result.analysis?.daily_miles_needed?.toLocaleString()} mi</p>
            </div>
            <div className="p-4 bg-[#121212]">
              <p className="text-white/50 text-xs uppercase">Loads Needed</p>
              <p className="text-xl font-bold text-white">{result.analysis?.loads_needed} loads</p>
            </div>
          </div>
          
          <div className="p-4 bg-green-500/10 border border-green-500/30">
            <p className="text-green-400 text-sm">
              Profit per mile after break-even: <span className="font-bold">${result.profit_per_mile}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Tools Page Component
const ToolsPage = () => {
  const navigate = useNavigate();
  const [tools, setTools] = useState({ available: [], locked: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState(null);
  const [userTier, setUserTier] = useState('basic');

  useEffect(() => {
    const fetchTools = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('hive_token');
        
        // Get user's tier from lifecycle status
        if (token) {
          try {
            const statusRes = await axios.get(`${API}/hive/me/lifecycle-status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setUserTier(statusRes.data?.build?.tier || 'basic');
          } catch (e) {
            // Use default tier
          }
        }
        
        // Fetch available tools
        const res = await axios.get(`${API}/build-system/tools?user_tier=${userTier}`);
        setTools(res.data);
      } catch (err) {
        console.error('Error fetching tools:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTools();
  }, [userTier]);

  const renderCalculator = () => {
    switch (selectedTool) {
      case 'rpm_calculator':
        return <RPMCalculator onClose={() => setSelectedTool(null)} />;
      case 'deadhead_calculator':
        return <DeadheadCalculator onClose={() => setSelectedTool(null)} />;
      case 'break_even_calculator':
        return <BreakEvenCalculator onClose={() => setSelectedTool(null)} />;
      default:
        return (
          <div className="text-center py-12">
            <Calculator className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl text-white font-bold">Coming Soon</h3>
            <p className="text-white/50">This tool is under development</p>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Crown className="w-12 h-12 text-[#D4AF37] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="tools-page">
      {/* Header */}
      <header className="bg-[#0D0D0D] border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/hive/dashboard')}
              className="flex items-center gap-2 text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  TOOLS & CALCULATORS
                </h1>
                <p className="text-white/50 text-sm">Business intelligence for trucking operators</p>
              </div>
            </div>
          </div>
          <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-sm uppercase font-bold">
            {userTier} Tier
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {selectedTool ? (
          <div>
            <button
              onClick={() => setSelectedTool(null)}
              className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tools
            </button>
            <div className="bg-[#121212] border border-white/10 p-6">
              {renderCalculator()}
            </div>
          </div>
        ) : (
          <>
            {/* Available Tools */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Available Tools ({tools.available?.length || 0})
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {tools.available?.map((tool) => (
                  <CalculatorCard
                    key={tool.tool_id}
                    tool={tool}
                    onClick={() => setSelectedTool(tool.tool_id)}
                    locked={false}
                  />
                ))}
              </div>
            </div>

            {/* Locked Tools */}
            {tools.locked?.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-zinc-500" />
                  Upgrade to Unlock ({tools.locked?.length})
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {tools.locked?.map((tool) => (
                    <CalculatorCard
                      key={tool.tool_id}
                      tool={tool}
                      onClick={() => {}}
                      locked={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ToolsPage;
